import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiSubmit, listRegistryLaborCos, ApiError, fieldErrorsFrom, BidFormPayload, PackageDetail, staffBidTemplateUrl, importStaffBidSheet, type RegistryLaborCo } from '../lib/api';
import { BidForm } from './BidForm';
import { Modal } from './ui/Modal';

interface NewEngResp {
  id: string;
  labor_co_name: string | null;
}

export function InternalBidEntry({ pkg, onClose }: { pkg: PackageDetail; onClose: () => void }) {
  const qc = useQueryClient();

  // Step 1: pick a supplier from the registry (or add a new one)
  const [mode, setMode] = useState<'pick' | 'new'>('pick');
  const activeEngagements = pkg.engagements.filter((e) => !e.withdrawn);
  const { data: registry } = useQuery<RegistryLaborCo[]>({ queryKey: ['registry-labor-cos'], queryFn: listRegistryLaborCos });
  const [pickedLc, setPickedLc] = useState('');
  const [filter, setFilter] = useState('');
  const [newName, setNewName] = useState('');
  const [step, setStep] = useState<1 | 2>(1);
  const [resolvedEngId, setResolvedEngId] = useState<string | null>(null);
  // labor_co_id → existing engagement id on this package (to reuse instead of duplicating)
  const engagedByLc = useMemo(
    () => new Map(activeEngagements.filter((e) => e.labor_co_id).map((e) => [e.labor_co_id as string, e.id])),
    [activeEngagements],
  );
  const q = filter.trim().toLowerCase();
  const filtered = (registry ?? []).filter((c) => !q || [c.name, c.internal_code, c.region, c.trades_json].some((v) => (v ?? '').toLowerCase().includes(q)));

  // Step 2: bid form state
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleProceed() {
    setBusy(true);
    setServerError(null);
    try {
      if (mode === 'new') {
        if (!newName.trim()) { setServerError('请输入劳务方名称 / Enter labor company name'); setBusy(false); return; }
        const eng = await api<NewEngResp>(`/packages/${pkg.id}/engagements`, {
          method: 'POST',
          body: JSON.stringify({ labor_co_name: newName.trim(), invite_method: 'manual' }),
        });
        setResolvedEngId(eng.id);
        await qc.invalidateQueries({ queryKey: ['package', pkg.id] });
      } else {
        if (!pickedLc) { setServerError('请选择一个劳务方 / Select a contractor'); setBusy(false); return; }
        const existing = engagedByLc.get(pickedLc);
        if (existing) {
          setResolvedEngId(existing); // already on this package → enter the next round
        } else {
          const eng = await api<NewEngResp>(`/packages/${pkg.id}/engagements`, {
            method: 'POST',
            body: JSON.stringify({ labor_co_id: pickedLc, invite_method: 'manual' }),
          });
          setResolvedEngId(eng.id);
          await qc.invalidateQueries({ queryKey: ['package', pkg.id] });
        }
      }
      setStep(2);
    } catch (e: any) {
      setServerError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(payload: BidFormPayload) {
    if (!resolvedEngId) return;
    setBusy(true);
    setServerError(null);
    setFieldErrors({});
    try {
      await apiSubmit(`/packages/${pkg.id}/engagements/${resolvedEngId}/bids`, { ...payload, source: 'manual' });
      await qc.invalidateQueries({ queryKey: ['package', pkg.id] });
      onClose();
    } catch (e: unknown) {
      const errMap = fieldErrorsFrom(e);
      setFieldErrors(errMap);
      setServerError(Object.keys(errMap).length ? null : ((e as ApiError)?.body?.error ?? (e as Error)?.message ?? '提交失败 / Submit failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} title="✍️ 录入报价 / Enter bid" width={960}>
        <div className="small muted" style={{ marginTop: -6, marginBottom: 10 }}>步骤 {step}/2 · 选劳务方 → 填报价</div>

        {step === 1 && (
          <div>
            <div className="row" style={{ gap: 10, marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={mode === 'pick'} onChange={() => setMode('pick')} />
                <span>选择已有劳务方 / Existing contractor</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} />
                <span>新增劳务方 / New contractor</span>
              </label>
            </div>

            {mode === 'pick' && (
              <div className="field">
                <label>劳务方 / Contractor *（来自供应商库 / from supplier database）</label>
                {(registry ?? []).length === 0 ? (
                  <div className="small muted">供应商库为空，请切换到"新增" / Registry empty — switch to "New".</div>
                ) : (
                  <>
                    <input type="text" placeholder="搜索：名称 / 编号 / 工种 / 地区 · search name / code / trade / region" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
                    <select value={pickedLc} onChange={(e) => setPickedLc(e.target.value)} size={8} style={{ width: '100%' }}>
                      <option value="">— 选择劳务方 / select（共 {filtered.length} 家）—</option>
                      {filtered.map((c) => {
                        let trades = '';
                        try { trades = c.trades_json ? (JSON.parse(c.trades_json) as string[]).join('、') : ''; } catch { /* ignore */ }
                        const label = (c.internal_code ? c.internal_code + ' · ' : '') + c.name
                          + (trades ? ' · ' + trades : '') + (c.region ? ' · ' + c.region : '')
                          + (c.status !== 'active' ? ` 【${c.status}】` : '') + (engagedByLc.has(c.id) ? ' ✓已邀请' : '');
                        return <option key={c.id} value={c.id}>{label}</option>;
                      })}
                    </select>
                  </>
                )}
              </div>
            )}

            {mode === 'new' && (
              <div className="field">
                <label>劳务方名称 / Company name *</label>
                <input
                  type="text"
                  placeholder="例如：建华劳务有限公司"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
            )}

            {serverError && (
              <div className="card" style={{ borderColor: 'var(--bad)', background: '#fce8e6', color: 'var(--bad)', fontSize: 13, marginBottom: 8 }}>
                {serverError}
              </div>
            )}

            <div className="row" style={{ marginTop: 12 }}>
              <span className="spacer" />
              <button className="btn-primary" disabled={busy} onClick={handleProceed}>
                {busy ? '请稍候…' : '下一步 / Next →'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (() => {
          const activeEng = resolvedEngId
            ? pkg.engagements.find((e) => e.id === resolvedEngId)
            : undefined;
          const latestBid = activeEng?.bids.length
            ? activeEng.bids.reduce((a, b) => (b.round_number > a.round_number ? b : a))
            : null;
          const prefill = latestBid
            ? {
                line_items: latestBid.line_items
                  .filter((li) => li.work_item_id != null)
                  .map((li) => ({
                    work_item_id: li.work_item_id as string,
                    unit_price_rmb: li.unit_price_rmb,
                    remarks_md: li.remarks_md,
                  })),
                terms: latestBid.terms as import('../lib/api').SubmitBidTerms | undefined,
              }
            : null;
          const roundNumber = (latestBid?.round_number ?? 0) + 1;
          return (
            <BidForm
              key={`${resolvedEngId}-${roundNumber}`}
              workItems={pkg.work_items}
              busy={busy}
              submitLabel="提交报价 / Submit bid"
              onSubmit={handleSubmit}
              serverError={serverError}
              fieldErrors={fieldErrors}
              prefill={prefill}
              roundNumber={roundNumber}
              exportUrl={resolvedEngId ? staffBidTemplateUrl(pkg.id, resolvedEngId) : undefined}
              onImportSheet={resolvedEngId ? (file) => importStaffBidSheet(pkg.id, resolvedEngId, file) : undefined}
            />
          );
        })()}
    </Modal>
  );
}
